import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Typography,
  Box,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Collapse
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import Layout from '../components/Layout';
import attendanceService from '../services/attendanceService';
import '../styles/LeaveApproval.css';

const LeaveApprovalPage = () => {
  // State management
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // View state
  const [activeTab, setActiveTab] = useState(0); // 0: pending, 1: all, 2: balances

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState(''); // 'approve' or 'reject'
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  // Expanded rows for details
  const [expandedRow, setExpandedRow] = useState(null);

  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0
  });

  // Fetch data on mount
  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch pending requests
      const pendingResponse = await attendanceService.getPendingLeaveRequests();
      setPendingRequests(pendingResponse.leave_requests || []);

      // Fetch all requests
      const allResponse = await attendanceService.getAllLeaveRequests();
      setLeaveRequests(allResponse.leave_requests || []);

      // Fetch leave balances
      const balancesResponse = await attendanceService.getAllLeaveBalances();
      setLeaveBalances(balancesResponse.leave_balances || []);

      // Calculate statistics
      const allRequests = allResponse.leave_requests || [];
      setStats({
        total: allRequests.length,
        pending: allRequests.filter(r => r.status === 'pending').length,
        approved: allRequests.filter(r => r.status === 'approved').length,
        rejected: allRequests.filter(r => r.status === 'rejected').length
      });

      setLoading(false);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.message || 'Failed to load leave data');
      setLoading(false);
    }
  };

  // Handle approve/reject dialog
  const openDialog = (type, request) => {
    setDialogType(type);
    setSelectedRequest(request);
    setAdminNotes('');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setDialogType('');
    setSelectedRequest(null);
    setAdminNotes('');
  };

  const handleSubmitAction = async () => {
    if (!selectedRequest) return;

    setProcessing(true);
    setError(null);

    try {
      if (dialogType === 'approve') {
        await attendanceService.approveLeaveRequest(selectedRequest.id, adminNotes);
        setSuccess(`Leave request #${selectedRequest.id} approved successfully!`);
      } else if (dialogType === 'reject') {
        if (!adminNotes.trim()) {
          setError('Please provide a reason for rejection');
          setProcessing(false);
          return;
        }
        await attendanceService.rejectLeaveRequest(selectedRequest.id, adminNotes);
        setSuccess(`Leave request #${selectedRequest.id} rejected successfully!`);
      }

      closeDialog();
      fetchAllData(); // Refresh data
      setProcessing(false);

      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error('Error processing request:', err);
      setError(err.response?.data?.message || 'Failed to process leave request');
      setProcessing(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  // Get leave type color
  const getLeaveTypeColor = (leaveType) => {
    switch (leaveType) {
      case 'CL': return 'primary';
      case 'SL': return 'secondary';
      case 'EL': return 'info';
      case 'LMD': return 'error';
      default: return 'default';
    }
  };

  // Render statistics cards
  const renderStatsCards = () => {
    return (
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card className="stats-card stats-card-total">
            <CardContent sx={{ textAlign: 'center', color: 'white' }}>
              <Typography variant="h3" sx={{ fontSize: '2.5rem', mb: 1 }}>
                📋
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 700, my: 2, fontSize: '2.5rem' }}>
                {stats.total}
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '0.95rem', fontWeight: 500 }}>
                Total Requests
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card className="stats-card stats-card-pending">
            <CardContent sx={{ textAlign: 'center', color: 'white' }}>
              <Typography variant="h3" sx={{ fontSize: '2.5rem', mb: 1 }}>
                ⏳
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 700, my: 2, fontSize: '2.5rem' }}>
                {stats.pending}
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '0.95rem', fontWeight: 500 }}>
                Pending Approval
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card className="stats-card stats-card-approved">
            <CardContent sx={{ textAlign: 'center', color: 'white' }}>
              <Typography variant="h3" sx={{ fontSize: '2.5rem', mb: 1 }}>
                ✅
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 700, my: 2, fontSize: '2.5rem' }}>
                {stats.approved}
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '0.95rem', fontWeight: 500 }}>
                Approved
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card className="stats-card stats-card-rejected">
            <CardContent sx={{ textAlign: 'center', color: 'white' }}>
              <Typography variant="h3" sx={{ fontSize: '2.5rem', mb: 1 }}>
                ❌
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 700, my: 2, fontSize: '2.5rem' }}>
                {stats.rejected}
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '0.95rem', fontWeight: 500 }}>
                Rejected
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  // Render leave requests table
  const renderRequestsTable = (requests, showActions = false) => {
    if (requests.length === 0) {
      return (
        <Alert severity="info">
          {showActions ? 'No pending leave requests at the moment.' : 'No leave requests found.'}
        </Alert>
      );
    }

    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Employee</TableCell>
              <TableCell>Leave Type</TableCell>
              <TableCell>From Date</TableCell>
              <TableCell>To Date</TableCell>
              <TableCell align="center">Days</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell>Requested On</TableCell>
              {showActions && <TableCell align="center">Actions</TableCell>}
              <TableCell align="center">Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.map((request) => (
              <React.Fragment key={request.id}>
                <TableRow hover>
                  <TableCell>#{request.id}</TableCell>
                  <TableCell>
                    <div>{request.employee_name}</div>
                    <Typography variant="caption" color="text.secondary">
                      {request.employee_code}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={`${request.leave_type_name} (${request.leave_type_code})`} 
                      color={getLeaveTypeColor(request.leave_type_code)} 
                      size="small" 
                    />
                  </TableCell>
                  <TableCell>{formatDate(request.from_date)}</TableCell>
                  <TableCell>{formatDate(request.to_date)}</TableCell>
                  <TableCell align="center">
                    <Chip label={request.number_of_days} color="primary" size="small" />
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={request.status.toUpperCase()} 
                      color={getStatusColor(request.status)} 
                      size="small" 
                    />
                  </TableCell>
                  <TableCell>{formatDate(request.created_at)}</TableCell>
                  {showActions && (
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={<ApproveIcon />}
                          onClick={() => openDialog('approve', request)}
                        >
                          Approve
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          color="error"
                          startIcon={<RejectIcon />}
                          onClick={() => openDialog('reject', request)}
                        >
                          Reject
                        </Button>
                      </Box>
                    </TableCell>
                  )}
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => setExpandedRow(expandedRow === request.id ? null : request.id)}
                    >
                      <ExpandMoreIcon 
                        sx={{ 
                          transform: expandedRow === request.id ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: '0.3s'
                        }} 
                      />
                    </IconButton>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={showActions ? 10 : 9} sx={{ p: 0 }}>
                    <Collapse in={expandedRow === request.id}>
                      <Box sx={{ p: 3, bgcolor: '#f8f9fa' }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Reason:
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                              {request.reason || 'No reason provided'}
                            </Typography>
                          </Grid>
                          {request.admin_notes && (
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" color="text.secondary">
                                Admin Notes:
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 2 }}>
                                {request.admin_notes}
                              </Typography>
                            </Grid>
                          )}
                          {request.processed_at && (
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" color="text.secondary">
                                Processed At:
                              </Typography>
                              <Typography variant="body2">
                                {formatDate(request.processed_at)}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Render leave balances table
  const renderBalancesTable = () => {
    if (leaveBalances.length === 0) {
      return <Alert severity="info">No leave balance data available.</Alert>;
    }

    // Group by employee
    const balancesByEmployee = leaveBalances.reduce((acc, balance) => {
      const empId = balance.employee_id;
      if (!acc[empId]) {
        acc[empId] = {
          employee_name: balance.employee_name,
          employee_code: balance.employee_code,
          balances: {}
        };
      }
      acc[empId].balances[balance.leave_type_code] = balance;
      return acc;
    }, {});

    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Employee</TableCell>
              <TableCell align="center">CL Balance</TableCell>
              <TableCell align="center">SL Balance</TableCell>
              <TableCell align="center">EL Balance</TableCell>
              <TableCell align="center">LMD Balance</TableCell>
              <TableCell align="center">Total Available</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.values(balancesByEmployee).map((emp, index) => {
              const clBalance = emp.balances.CL || {};
              const slBalance = emp.balances.SL || {};
              const elBalance = emp.balances.EL || {};
              const lmdBalance = emp.balances.LMD || {};
              
              const totalAvailable = 
                (clBalance.available_days || 0) +
                (slBalance.available_days || 0) +
                (elBalance.available_days || 0);

              return (
                <TableRow key={index} hover>
                  <TableCell>
                    <div>{emp.employee_name}</div>
                    <Typography variant="caption" color="text.secondary">
                      {emp.employee_code}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box>
                      <Chip 
                        label={`${clBalance.available_days || 0} / ${clBalance.total_days || 0}`} 
                        color="primary" 
                        size="small" 
                      />
                      <Typography variant="caption" display="block" color="text.secondary">
                        Used: {clBalance.used_days || 0}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box>
                      <Chip 
                        label={`${slBalance.available_days || 0} / ${slBalance.total_days || 0}`} 
                        color="secondary" 
                        size="small" 
                      />
                      <Typography variant="caption" display="block" color="text.secondary">
                        Used: {slBalance.used_days || 0}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box>
                      <Chip 
                        label={`${elBalance.available_days || 0} / ${elBalance.total_days || 0}`} 
                        color="info" 
                        size="small" 
                      />
                      <Typography variant="caption" display="block" color="text.secondary">
                        Used: {elBalance.used_days || 0}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box>
                      <Chip 
                        label={lmdBalance.available_days || 0} 
                        color="error" 
                        size="small" 
                      />
                      <Typography variant="caption" display="block" color="text.secondary">
                        Auto-deducted
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={`${totalAvailable} days`} 
                      color="success" 
                      size="small" 
                      sx={{ fontWeight: 600 }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Main render
  return (
    <Layout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ mb: 4, borderBottom: 2, borderColor: 'divider', pb: 2 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
            Leave Approval Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review and approve/reject employee leave requests, monitor leave balances
          </Typography>
        </Box>

        {/* Success/Error Messages */}
        {success && (
          <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5 }}>
            <CircularProgress size={60} />
            <Typography sx={{ mt: 3 }}>Loading leave data...</Typography>
          </Box>
        )}

        {/* Data Display */}
        {!loading && (
          <>
            {/* Statistics Cards */}
            {renderStatsCards()}

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs 
                value={activeTab} 
                onChange={(e, newValue) => setActiveTab(newValue)}
                variant="fullWidth"
              >
                <Tab label={`⏳ Pending Approval (${stats.pending})`} />
                <Tab label="📋 All Requests" />
                <Tab label="💼 Leave Balances" />
              </Tabs>
            </Box>

            {/* Tab Content */}
            <Card>
              <CardHeader 
                title={
                  activeTab === 0 ? 'Pending Leave Requests' :
                  activeTab === 1 ? 'All Leave Requests' :
                  'Employee Leave Balances'
                }
              />
              <CardContent>
                {activeTab === 0 && renderRequestsTable(pendingRequests, true)}
                {activeTab === 1 && renderRequestsTable(leaveRequests, false)}
                {activeTab === 2 && renderBalancesTable()}
              </CardContent>
            </Card>
          </>
        )}

        {/* Approve/Reject Dialog */}
        <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {dialogType === 'approve' ? '✅ Approve Leave Request' : '❌ Reject Leave Request'}
          </DialogTitle>
          <DialogContent>
            {selectedRequest && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Employee: <strong>{selectedRequest.employee_name}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Leave Type: <strong>{selectedRequest.leave_type_name}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Duration: <strong>{formatDate(selectedRequest.from_date)} to {formatDate(selectedRequest.to_date)}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Days: <strong>{selectedRequest.number_of_days}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Reason: <em>{selectedRequest.reason || 'Not provided'}</em>
                </Typography>
              </Box>
            )}

            <TextField
              fullWidth
              multiline
              rows={4}
              label={dialogType === 'approve' ? 'Admin Notes (Optional)' : 'Reason for Rejection (Required)'}
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder={
                dialogType === 'approve' 
                  ? 'Add any notes or conditions...' 
                  : 'Please provide a reason for rejection...'
              }
              required={dialogType === 'reject'}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog} disabled={processing}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitAction}
              variant="contained"
              color={dialogType === 'approve' ? 'success' : 'error'}
              disabled={processing}
              startIcon={processing ? <CircularProgress size={20} /> : null}
            >
              {processing ? 'Processing...' : (dialogType === 'approve' ? 'Approve' : 'Reject')}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default LeaveApprovalPage;
